import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, UiBox, UiButton, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {SEXUAL_HEALTH_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[SEXUAL_HEALTH_KEYS.STATEMENT_BEST_APPLIES]?.data,
  );

  const {questionnaires} = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaire = questionnaires?.find(i => i.key === SEXUAL_HEALTH_KEYS.STATEMENT_BEST_APPLIES);
  const {inputs, setInputs} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    relations: currentAnswer?.relations || [],
    previousSymptoms: currentAnswer?.previousSymptoms || [],
  });

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: SEXUAL_HEALTH_KEYS.STATEMENT_BEST_APPLIES,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: SEXUAL_HEALTH_KEYS.SELECT_SYMPTOMS}));
  };

  const renderQuestion = () => (
    <UiBox className="animate__animated animate__fadeInLeft">
      {inputs?.relations?.map((val: Relation) => (
        <UiBox key={val?.questions?.default}>
          <UiText fontSize="md" fontWeight="bold" mt={spacing.large}>
            {val?.questions?.default}
          </UiText>
          <UiSimpleGrid columns={2} spacing={5} mt={spacing.small}>
            {val?.options?.map(option => (
              <UiButton
                key={`${option.label}-${option.value}`}
                title={option.label}
                variant={inputs.previousSymptoms.includes(option.value) ? 'solid' : 'toggle'}
                onClick={() => setInputs({previousSymptoms: [option.value]})}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
      ))}
    </UiBox>
  );

  return (
    <>
      <UiQuestionnaireLayout
        backIcon
        disableNext={!inputs.selectedOption || (inputs.selectedOption === 2 && !inputs.previousSymptoms.length)}
        handleOnNext={onClickSubmit}
        actions={questionnaire?.actions}
        checkoutStep={questionnaire?.checkoutStep}
      >
        <Col alignItems="center" textAlign="center">
          <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
          <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
            {questionnaire?.questions?.default}
          </UiText>
          <UiSimpleGrid columns={1} spacing={5} mt={spacing.small}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value, relations: value.relations})}
              />
            ))}
          </UiSimpleGrid>
          {inputs.selectedOption === 2 && renderQuestion()}
        </Col>
      </UiQuestionnaireLayout>
    </>
  );
};

export default Questionnaire4Screen;
